import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Spin, Alert, notification } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useEvent } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { setSelectedVisit } from 'store/slices/visitsSlice';
import { VisitorSearch } from 'types/visitorSearchTypes';
import { getRequest, postRequest } from 'api/apiClient';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'utils/utils';

interface ScanQrCodeProps {
    open: boolean;
    visitorsToday: VisitorSearch[];
    onClose: () => void;
    openVisitorsDrawer: () => void;
}

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const ScanQRCodeModal: React.FC<ScanQrCodeProps> = ({
    open,
    visitorsToday,
    onClose,
    openVisitorsDrawer,
}) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasAllvisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');
    const scannedDataRef = useRef('');
    const [scanningInProgress, setScanningInProgress] = useState(false);
    const [isKeyPressed, setIsKeyPressed] = useState(false);

    const timerRef = useRef<NodeJS.Timeout | null>(null); // Timer to detect the end of scanning

    const { t } = useTranslation();
    useEvent('keydown', scanningInProgress ? (event: Event) => handleKeyDown(event as KeyboardEvent) : null, document);

    useEffect(() => {
        if (open) {
            setScanningInProgress(true);
        } else {
            setScanningInProgress(false);
            setIsKeyPressed(false);
            scannedDataRef.current = '';
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        }
    }, [open]);

    const handleScannedData = useCallback(async (data: string) => {
        console.log('Scanned Data:', data);

        let tokenToUse = data;
        let qrCodeId = null;

        if (data.startsWith('S-')) {
            try {
                const response = await getRequest(`/orgs/${orgId}/qrcode?code=${data}`);
                if (response?.data?.length > 0 && response.data[0].visitorToken) {
                    const qrData = response.data[0];

                    if (qrData.expiresUtc) {
                        const expirationTime = dayjs(qrData.expiresUtc).utc(true).local();
                        console.log('expirationTime', expirationTime);
                        if (expirationTime.isBefore(dayjs())) {
                            notification.error({
                                message: t('scanQrCodeModal.notification.expired.title'),
                                description: t('scanQrCodeModal.notification.expired.description'),
                                placement: 'bottomRight',
                            });
                            return;
                        }
                    }

                    tokenToUse = qrData.visitorToken;
                    qrCodeId = qrData.id;
                } else {
                    notification.error({
                        message: t('scanQrCodeModal.notification.invalid.title'),
                        description: t('scanQrCodeModal.notification.invalid.description'),
                        placement: 'bottomRight',
                    });
                    return;
                }
            } catch (error) {
                notification.error({
                    message: t('scanQrCodeModal.notification.invalid.title'),
                    description: t('scanQrCodeModal.notification.invalid.description'),
                    placement: 'bottomRight',
                });
                console.error('Request failed:', error);
                return;
            }
        }

        try {
            const response = await fetch(`${apiUrl}/orgs/${orgId}/search/registration?token=${tokenToUse}`, {
                method: 'GET',
                headers: {
                    Authorization: `${localStorage.getItem('authToken')}`,
                    Accept: 'application/json',
                },
            });

            if (response.ok) {
                const registrationTokenResponse = await axios.post(`${apiUrl}/auth/registrationTokens/${tokenToUse}/validate`);
                const selectedVisitor = visitorsToday.find(v =>
                    Number(v.id) === Number(registrationTokenResponse.data.data.data.visitorId) &&
                    // When 'All Visitor' scope is OFF, only match visitors assigned to the logged-in user
                    (hasAllvisitorsWrite || v.hostUserId === globalUserId)
                );
                if (selectedVisitor === undefined) {
                    notification.error({
                        message: t('scanQrCodeModal.notification.noVisitor.title'),
                        description: t('scanQrCodeModal.notification.noVisitor.description'),
                        placement: 'bottomRight',
                    });
                } else {
                    const selectedVisit = await getRequest(`/orgs/${orgId}/visitor/${selectedVisitor.visitId}`);
                    dispatch(setSelectedVisit(selectedVisit.data[0]));
                    openVisitorsDrawer();

                    try {
                        await postRequest(`/orgs/${orgId}/visitor/${selectedVisitor.visitId}/qrScanLog`, {
                            visitorId: selectedVisitor.id,
                            source: "Web Control Center",
                            qrCodeId: qrCodeId,
                            qrValue: data,
                        });
                    } catch (error) {
                        notification.error({
                            message: t('scanQrCodeModal.notification.logFail.title'),
                            description: t('scanQrCodeModal.notification.logFail.description'),
                            placement: 'bottomRight',
                        });
                        console.error('QR scan log request failed:', error);
                    }

                }
            } else {
                notification.error({
                    message: t('scanQrCodeModal.notification.scanFail.title'),
                    description: t('scanQrCodeModal.notification.scanFail.description'),
                    placement: 'bottomRight',
                });
            }
        } catch (error) {
            notification.error({
                message: t('scanQrCodeModal.notification.scanFail.title'),
                description: t('scanQrCodeModal.notification.scanFail.description'),
                placement: 'bottomRight',
            });
            console.error('Request failed:', error);
        }
    }, [dispatch, openVisitorsDrawer, t, orgId, visitorsToday, hasAllvisitorsWrite, globalUserId]);


    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        setIsKeyPressed(true);

        if (event.key.length === 1) {
            scannedDataRef.current += event.key;
        }

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        
        timerRef.current = setTimeout(() => {
            setScanningInProgress(false);
            setIsKeyPressed(false);
            handleScannedData(scannedDataRef.current);
            scannedDataRef.current = '';
            onClose();
        }, 1000);
    }, [onClose, handleScannedData]);

    return (
        <Modal
            title={t('scanQrCodeModal.title')}
            open={open}
            onOk={onClose}
            onCancel={() => {
                scannedDataRef.current = '';
                setScanningInProgress(false);
                setIsKeyPressed(false);
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                onClose();
            }}
            okText={t('scanQrCodeModal.confirm')}
            cancelText={t('scanQrCodeModal.cancel')}
            centered
            style={{ textAlign: 'center' }}
            footer={null}
        >
            {scanningInProgress ? <Spin indicator={<LoadingOutlined spin />} size="default" style={{ padding: '20px' }} /> : undefined}
            {isKeyPressed ? <Alert message={t('scanQrCodeModal.alert.processing')} type="success" /> : <Alert message={t('scanQrCodeModal.alert.waiting')} type="info" />}
        </Modal>
    );
};

export default ScanQRCodeModal;
